import { useState } from 'react';
import {
  Content,
  ContentHeader,
  Header,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
  StatusWarning,
  SubvalueCell,
  SupportButton,
  Table,
  TableColumn,
  TableState,
  WarningPanel,
} from '@backstage/core-components';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import {
  formatDate,
  PackageSummary,
  PackageUsage,
  scopeLabel,
  usePackages,
} from './api';
import { FiltersOnRight } from './FiltersOnRight';
import { PackageStatus, statusLabel } from './PackageStatus';

const REGISTRY_URL =
  'https://github.com/atg-digital/backstage/blob/main/catalog/package-registry.yaml';
const RECENT_DAYS = 30;

type PackageRow = PackageSummary & {
  id: string;
  statusLabel: string;
  repoCount: number;
  /** Distinct versions in use across every repository. */
  versionCount: number;
  licenseLabel: string;
  scopeLabel: string;
  /** Repositories that use the package at runtime rather than only in development. */
  runtimeRepoCount: number;
};

type UsageRow = PackageUsage & {
  id: string;
  name: string;
  ecosystem: string;
  pkg: PackageSummary;
};

/** One registry entry within a capability: a package, or a family such as `@aws-sdk/*`. */
type EntryUsage = {
  name: string;
  status?: PackageRow['status'];
  packageCount: number;
  repoCount: number;
};

type CapabilityRow = { id: string; capability: string; entries: EntryUsage[] };

type TabId = 'all' | 'duplicates' | 'unpinned' | 'copyleft' | 'added';

const useStyles = makeStyles(theme => ({
  tile: { height: '100%' },
  tileSelected: {
    height: '100%',
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
  },
  // Top-align tile content so the numbers line up whatever the text length.
  tileArea: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
  clamp: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  tabs: { borderBottom: `1px solid ${theme.palette.divider}` },
  nowrap: { whiteSpace: 'nowrap' },
}));

const repos = (count: number) => `${count} ${count === 1 ? 'repo' : 'repos'}`;

const repoLink = (repo: string) => (
  <Link to={`https://github.com/${repo}`}>{repo}</Link>
);

// Owners come from spec.owner as written, often a bare team name, which
// EntityRefLink rejects; resolve it to a full group reference first.
const ownerLink = (owner: string | null) =>
  owner ? (
    <EntityRefLink
      entityRef={stringifyEntityRef(
        parseEntityRef(owner, {
          defaultKind: 'group',
          defaultNamespace: 'default',
        }),
      )}
    />
  ) : (
    '—'
  );

/** A licence expression, clamped to two lines with the full text on hover. */
const License = ({ label, copyleft }: { label: string; copyleft: boolean }) => {
  const classes = useStyles();
  const text = (
    <span className={classes.clamp} title={label}>
      {label}
    </span>
  );
  return copyleft ? <StatusWarning>{text} (copyleft)</StatusWarning> : text;
};

const StatTile = (props: {
  value: number;
  label: string;
  detail: string;
  selected: boolean;
  onClick: () => void;
}) => {
  const classes = useStyles();
  return (
    <Card
      variant="outlined"
      className={props.selected ? classes.tileSelected : classes.tile}
    >
      <CardActionArea
        className={classes.tileArea}
        onClick={props.onClick}
        aria-pressed={props.selected}
      >
        <CardContent>
          <Typography variant="h3" component="p">
            {props.value.toLocaleString()}
          </Typography>
          <Typography variant="subtitle1" component="p">
            {props.label}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {props.detail}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const usageColumns: TableColumn<PackageUsage>[] = [
  { title: 'Repository', render: u => repoLink(u.repo) },
  { title: 'Owner', render: u => ownerLink(u.owner) },
  { title: 'Used as', render: u => scopeLabel(u.scope) },
  {
    title: 'Versions',
    render: u =>
      u.unpinned ? (
        <StatusWarning>
          {u.versions.join(', ')} (not pinned to a SHA)
        </StatusWarning>
      ) : (
        u.versions.join(', ')
      ),
  },
  { title: 'Licence', render: u => u.licenses.join(', ') || '—' },
  {
    title: 'Added',
    render: u => (u.firstSeen ? formatDate(u.firstSeen) : 'Before first scan'),
  },
];

const nowrap = { whiteSpace: 'nowrap' } as const;

const packageColumns: TableColumn<PackageRow>[] = [
  {
    title: 'Package',
    field: 'name',
    highlight: true,
    cellStyle: { wordBreak: 'break-word', minWidth: 200 },
    render: row => <SubvalueCell value={row.name} subvalue={row.ecosystem} />,
  },
  // Shown under the package name; kept as a hidden column for the filter.
  { title: 'Ecosystem', field: 'ecosystem', hidden: true },
  {
    title: 'Used as',
    field: 'scopeLabel',
    render: row =>
      row.scope === 'runtime' && row.runtimeRepoCount < row.repoCount
        ? `Runtime in ${row.runtimeRepoCount} of ${row.repoCount}`
        : row.scopeLabel,
  },
  {
    title: 'Status',
    field: 'statusLabel',
    cellStyle: nowrap,
    render: row => <PackageStatus status={row.status} />,
  },
  { title: 'Capability', field: 'capability', emptyValue: '—' },
  {
    title: 'Repos',
    field: 'repoCount',
    type: 'numeric',
    defaultSort: 'desc',
  },
  { title: 'Versions', field: 'versionCount', type: 'numeric' },
  {
    title: 'Licence',
    field: 'licenseLabel',
    render: row => <License label={row.licenseLabel} copyleft={row.copyleft} />,
  },
];

const UNREVIEWED_RUNTIME: TableState = {
  filtersOpen: true,
  filters: { 'Used as': ['Runtime'], Status: ['Unreviewed'] },
};

export const PackagesPage = () => {
  const classes = useStyles();
  const { value, loading, error } = usePackages();
  const [tab, setTab] = useState<TabId>('all');
  // Changing this remounts the All packages table with preset filters.
  const [allState, setAllState] = useState<{ key: number; state?: TableState }>(
    { key: 0 },
  );

  let body;
  if (loading) {
    body = <Progress />;
  } else if (error) {
    body = <ResponseErrorPanel error={error} />;
  } else if (value) {
    const rows: PackageRow[] = value.packages.map(pkg => ({
      ...pkg,
      id: `${pkg.ecosystem}:${pkg.name}`,
      statusLabel: statusLabel(pkg.status),
      repoCount: pkg.usages.length,
      versionCount: new Set(pkg.usages.flatMap(u => u.versions)).size,
      licenseLabel: pkg.licenses.join(', ') || '—',
      scopeLabel: scopeLabel(pkg.scope),
      runtimeRepoCount: pkg.usages.filter(u => u.scope === 'runtime').length,
    }));

    // A capability is duplicated when more than one registry entry serves it.
    // Packages matched by the same family entry (every @aws-sdk/* client) are
    // one choice, not duplicates of each other.
    const byCapability = new Map<string, Map<string, PackageRow[]>>();
    for (const row of rows) {
      if (!row.capability) continue;
      const entries = byCapability.get(row.capability) ?? new Map();
      const entry = row.registryName ?? row.name;
      entries.set(entry, [...(entries.get(entry) ?? []), row]);
      byCapability.set(row.capability, entries);
    }
    const duplicates: CapabilityRow[] = [...byCapability]
      .filter(([, entries]) => entries.size > 1)
      .map(([capability, entries]) => ({
        id: capability,
        capability,
        entries: [...entries]
          .map(([name, packages]) => ({
            name,
            status: packages[0].status,
            packageCount: packages.length,
            repoCount: new Set(packages.flatMap(p => p.usages.map(u => u.repo)))
              .size,
          }))
          .sort((a, b) => b.repoCount - a.repoCount),
      }))
      // Widest-used first: those are the choices worth making first.
      .sort(
        (a, b) =>
          b.entries.reduce((n, e) => n + e.repoCount, 0) -
          a.entries.reduce((n, e) => n + e.repoCount, 0),
      );

    const since = new Date(Date.now() - RECENT_DAYS * 86_400_000).toISOString();
    const usages: UsageRow[] = rows.flatMap(pkg =>
      pkg.usages.map(u => ({
        ...u,
        id: `${pkg.id}:${u.repo}`,
        name: pkg.name,
        ecosystem: pkg.ecosystem,
        pkg,
      })),
    );
    const additions = usages
      .filter(u => u.firstSeen && u.firstSeen >= since)
      .sort((a, b) => b.firstSeen!.localeCompare(a.firstSeen!));
    const unpinned = usages.filter(u => u.unpinned);
    const copyleft = rows.filter(r => r.copyleft);

    const scannedRepos = value.repos.filter(r => !r.error).length;
    const failedScans = value.repos.filter(r => r.error);
    const unreviewed = rows.filter(r => !r.status).length;
    const unreviewedRuntime = rows.filter(
      r => !r.status && r.scope === 'runtime',
    ).length;

    const showAll = (state?: TableState) => {
      setTab('all');
      setAllState(current => ({ key: current.key + 1, state }));
    };

    const tiles = [
      {
        id: 'all',
        value: rows.length,
        label: 'Packages in use',
        detail: `Across ${scannedRepos} scanned repositories`,
        onClick: () => showAll(),
      },
      {
        id: 'unreviewed-runtime',
        value: unreviewedRuntime,
        label: 'Unreviewed at runtime',
        detail: `Of ${unreviewed} unreviewed; review these first`,
        onClick: () => showAll(UNREVIEWED_RUNTIME),
      },
      {
        id: 'duplicates',
        value: duplicates.length,
        label: 'Duplicated capabilities',
        detail: 'More than one package for the same job',
        onClick: () => setTab('duplicates'),
      },
      {
        id: 'unpinned',
        value: unpinned.length,
        label: 'Unpinned actions',
        detail: `In ${new Set(unpinned.map(u => u.repo)).size} repositories`,
        onClick: () => setTab('unpinned'),
      },
      {
        id: 'copyleft',
        value: copyleft.length,
        label: 'Copyleft licences',
        detail: 'GPL, AGPL or SSPL',
        onClick: () => setTab('copyleft'),
      },
      {
        id: 'added',
        value: additions.length,
        label: 'Recently added',
        detail: `In the last ${RECENT_DAYS} days`,
        onClick: () => setTab('added'),
      },
    ];
    const selectedTile =
      tab === 'all' && allState.state ? 'unreviewed-runtime' : tab;

    let panel;
    if (tab === 'duplicates') {
      panel = (
        <Table<CapabilityRow>
          title="Capabilities served by more than one package"
          subtitle="Pick one per capability and move the rest to hold or deprecated in the registry"
          options={{ paging: false, emptyRowsWhenPaging: false, search: false }}
          data={duplicates}
          emptyContent={
            <Box p={2}>
              <Typography>
                No capability has more than one package. Add a capability to
                registry entries to find duplicates.
              </Typography>
            </Box>
          }
          columns={[
            {
              title: 'Capability',
              field: 'capability',
              highlight: true,
              width: '20%',
            },
            {
              title: 'Packages in use',
              render: row => (
                <Box display="flex" flexWrap="wrap" style={{ gap: 24 }}>
                  {row.entries.map(e => (
                    <PackageStatus key={e.name} status={e.status}>
                      {e.name}
                      {e.packageCount > 1 &&
                        ` (${e.packageCount} packages)`} ·{' '}
                      {statusLabel(e.status)} · {repos(e.repoCount)}
                    </PackageStatus>
                  ))}
                </Box>
              ),
            },
          ]}
        />
      );
    } else if (tab === 'unpinned') {
      panel = (
        <Table<UsageRow>
          title="Third-party GitHub Actions not pinned to a commit SHA"
          subtitle="Whoever controls the action's tags can change what these workflows run"
          options={{ pageSize: 20, padding: 'dense' }}
          data={unpinned}
          columns={[
            { title: 'Action', field: 'name', highlight: true },
            { title: 'Reference', render: row => row.versions.join(', ') },
            {
              title: 'Repository',
              field: 'repo',
              render: row => repoLink(row.repo),
            },
            { title: 'Owner', render: row => ownerLink(row.owner) },
          ]}
        />
      );
    } else if (tab === 'copyleft') {
      panel = (
        <Table<PackageRow>
          title="Packages with a copyleft licence"
          subtitle="Only repositories left with no permissive licence option are listed"
          // Fixed layout so the widths below apply; licence expressions are long.
          options={{
            paging: false,
            emptyRowsWhenPaging: false,
            search: false,
            tableLayout: 'fixed',
          }}
          data={copyleft}
          columns={[
            {
              title: 'Package',
              field: 'name',
              highlight: true,
              width: '20%',
              render: row => (
                <SubvalueCell value={row.name} subvalue={row.ecosystem} />
              ),
            },
            { title: 'Licence', field: 'licenseLabel', width: '45%' },
            {
              title: 'Repositories',
              width: '35%',
              render: row => (
                <>
                  {row.usages
                    .filter(u => u.copyleft)
                    .map(u => (
                      <div key={u.repo} className={classes.nowrap}>
                        {repoLink(u.repo)}
                      </div>
                    ))}
                </>
              ),
            },
          ]}
        />
      );
    } else if (tab === 'added') {
      panel = (
        <Table<UsageRow>
          title={`Added in the last ${RECENT_DAYS} days`}
          subtitle="Dependencies that appeared in a repository since its first scan"
          options={{ pageSize: 20, padding: 'dense' }}
          data={additions}
          emptyContent={
            <Box p={2}>
              <Typography>
                Nothing added in the last {RECENT_DAYS} days. Dependencies
                present at a repository's first scan count as the baseline.
              </Typography>
            </Box>
          }
          columns={[
            {
              title: 'Package',
              field: 'name',
              highlight: true,
              render: row => (
                <SubvalueCell value={row.name} subvalue={row.ecosystem} />
              ),
            },
            { title: 'Used as', render: row => scopeLabel(row.scope) },
            {
              title: 'Status',
              cellStyle: nowrap,
              render: row => <PackageStatus status={row.pkg.status} />,
            },
            {
              title: 'Repository',
              field: 'repo',
              render: row => repoLink(row.repo),
            },
            { title: 'Owner', render: row => ownerLink(row.owner) },
            { title: 'Added', render: row => formatDate(row.firstSeen!) },
          ]}
        />
      );
    } else {
      panel = (
        <FiltersOnRight>
          <Table<PackageRow>
            key={allState.key}
            title="All packages"
            initialState={allState.state}
            options={{
              pageSize: 25,
              padding: 'dense',
              headerStyle: nowrap,
            }}
            data={rows}
            columns={packageColumns}
            filters={[
              { column: 'Ecosystem', type: 'multiple-select' },
              { column: 'Used as', type: 'multiple-select' },
              { column: 'Status', type: 'multiple-select' },
              { column: 'Capability', type: 'select' },
            ]}
            detailPanel={({ rowData }) => (
              <Box px={2} pb={2}>
                <Table<PackageUsage>
                  options={{
                    paging: false,
                    emptyRowsWhenPaging: false,
                    search: false,
                    toolbar: false,
                  }}
                  data={rowData.usages}
                  columns={usageColumns}
                />
              </Box>
            )}
          />
        </FiltersOnRight>
      );
    }

    body = (
      <Grid container spacing={3}>
        {value.registryError && (
          <Grid item xs={12}>
            <WarningPanel
              title="Could not read the package registry, so every package shows as unreviewed"
              message={value.registryError}
            />
          </Grid>
        )}
        {failedScans.length > 0 && (
          <Grid item xs={12}>
            <WarningPanel
              title={`${failedScans.length} of ${value.repos.length} repositories could not be scanned`}
            >
              {failedScans.map(r => (
                <Typography key={r.repo} variant="body2">
                  {r.repo}: {r.error}
                </Typography>
              ))}
            </WarningPanel>
          </Grid>
        )}
        {tiles.map(tile => (
          <Grid key={tile.id} item xs={6} sm={4} lg={2}>
            <StatTile {...tile} selected={selectedTile === tile.id} />
          </Grid>
        ))}
        <Grid item xs={12}>
          <Tabs
            className={classes.tabs}
            value={tab}
            onChange={(_, next: TabId) =>
              next === 'all' ? showAll() : setTab(next)
            }
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
          >
            <Tab value="all" label={`All packages (${rows.length})`} />
            <Tab
              value="duplicates"
              label={`Duplicates (${duplicates.length})`}
            />
            <Tab
              value="unpinned"
              label={`Unpinned actions (${unpinned.length})`}
            />
            <Tab value="copyleft" label={`Copyleft (${copyleft.length})`} />
            <Tab value="added" label={`Recently added (${additions.length})`} />
          </Tabs>
          {/* Keyed by tab so each tab gets a fresh table: material-table keeps
              paging state across a data change, which breaks on switching. */}
          <Box key={tab} mt={3}>
            {panel}
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Page themeId="tool">
      <Header
        title="Packages"
        subtitle="Direct dependencies of every catalogued repository"
      />
      <Content>
        <ContentHeader title="">
          <Typography variant="body2" className={classes.nowrap}>
            To review a package, add it to the{' '}
            <Link to={REGISTRY_URL}>package registry</Link>.
          </Typography>
          <SupportButton>
            Read in hourly batches from each repository's GitHub dependency
            graph and checked against the package registry in catalog/.
          </SupportButton>
        </ContentHeader>
        {body}
      </Content>
    </Page>
  );
};
